import { FormTypeEnum } from '@elearning-platform/api-types/enums';
import { AgreementModelInterface } from '@elearning-platform/api-types/interfaces';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { getAgreements } from '../../../../api/requests';
import { QueryKey } from '../../../../enums';

import { MissingProfileDataFieldType } from './missingProfileData.enums';
import { MissingProfileDataFields, MissingProfileFormData, UseMissingProfileData } from './missingProfileData.types';

export const useMissingProfileData: () => UseMissingProfileData = (): UseMissingProfileData => {
  const { data: agreements }: UseQueryResult<AgreementModelInterface[]> = useQuery({
    queryKey: [QueryKey.Agreements],
    queryFn: (): Promise<AgreementModelInterface[]> => getAgreements(),
  });

  const agreementsRecord: Record<string, AgreementModelInterface> = useMemo(
    (): Record<string, AgreementModelInterface> => agreements?.reduce(
      (acc: Record<string, AgreementModelInterface>, agreement: AgreementModelInterface): Record<string, AgreementModelInterface> => {
        acc[agreement.name] = agreement;

        return acc;
      },
      {}
    ) ?? {},
    [agreements]
  );

  // TODO: add types after login request available in types package
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getConfig: (formType: string, data: any) => MissingProfileDataFields | null = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (formType: string, data: any): MissingProfileDataFields | null => {
      switch (formType) {
        case 'setPrivateEmail':
          return {
            form: [
              {
                fieldType: MissingProfileDataFieldType.Email,
                label: 'competence.app.actionRequiredToAccess.missingProfileData.configs.setPrivateEmail.valueLabel',
                name: 'value',
                required: true,
              },
              ...data.requiredAgreements.map((requiredAgreementName: string): MissingProfileFormData => ({
                fieldType: MissingProfileDataFieldType.BooleanType,
                label: agreementsRecord[requiredAgreementName]?.content,
                name: requiredAgreementName,
                required: true,
              })),
            ],
            title: 'competence.app.actionRequiredToAccess.missingProfileData.configs.setPrivateEmail.title',
            infoBox: [FormTypeEnum.ContractAgreementUserB2B, FormTypeEnum.ContractAgreementAdminB2B].includes(data.contractFormType)
              ? 'toyota.app.actionRequiredToAccess.missingProfileData.configs.setPrivateEmail.infoBoxB2b'
              : 'toyota.app.actionRequiredToAccess.missingProfileData.configs.setPrivateEmail.infoBox',
            successMessage: 'competence.app.actionRequiredToAccess.missingProfileData.configs.setPrivateEmail.successMessage',
            successDescription: 'competence.app.actionRequiredToAccess.missingProfileData.configs.setPrivateEmail.successDescription',
          };
        default:
          return null;
      }
    },
    [agreementsRecord]
  );

  return { getConfig };
};
